import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ClipboardList,
  Clock,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  LinkIcon,
  Lock,
  RefreshCw,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import surveyService from '../../services/surveyService';
import { AuthContext } from '../../contexts/AuthContext';
import DateMocker from '../Shared/DateMocker';
import * as versionService from '../../services/versionService';

// Stat Card
const StatCard = ({ icon, label, value, accent }) => {
  const accentMap = {
    primary: {
      iconWrap: 'bg-primary-50 text-primary-600',
      value: 'text-primary-700',
    },
    emerald: {
      iconWrap: 'bg-emerald-50 text-emerald-600',
      value: 'text-emerald-700',
    },
    amber: {
      iconWrap: 'bg-amber-50 text-amber-600',
      value: 'text-amber-700',
    },
  };

  const tone = accentMap[accent] || accentMap.primary;

  return (
    <motion.div
      whileHover={{ y: -1 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 p-4"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {label}
          </p>
          <p className={`text-3xl leading-none font-semibold tabular-nums ${tone.value}`}>
            {value}
          </p>
        </div>

        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tone.iconWrap}`}>
          {icon}
        </div>
      </div>
    </motion.div>
  );
};

// Section Header
const SectionHeader = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-5">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  </div>
);

// Survey Card
const SurveyCard = ({ assignment, statusInfo, onClick, hideStatusLabel, isDeactivated }) => {
  const isLocked = assignment.status === 'completed' && !assignment.is_patient_initiated;
  // FECOA-1333: strict within-timepoint ordering. The API locks this survey
  // until the earlier assessments in its timepoint are completed; show it but
  // don't let the participant start it.
  const isTimepointLocked = !!assignment.timepoint_locked;
  // FECOA-1513: scheduled onsite assessments are shown but cannot be started
  // from the web dashboard (the participant must be onsite). Treat them as
  // non-interactive like a locked completed card.
  const isDisabled = isLocked || isDeactivated || isTimepointLocked;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      whileHover={!isDisabled ? { x: 2, transition: { duration: 0.2 } } : {}}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300
        ${isDisabled ? 'opacity-60' : 'hover:shadow-md cursor-pointer'} group`}
      onClick={(isDeactivated || isTimepointLocked) ? undefined : onClick}
      aria-disabled={isDeactivated || isTimepointLocked || undefined}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center flex-1 min-w-0">
          <div className={`${statusInfo.bgColor} p-2.5 rounded-full mr-4 flex-shrink-0`}>
            {statusInfo.icon}
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-300 truncate flex items-center">
              {assignment.survey_name}
              {assignment.linkedCount > 0 && (
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-600 ml-2 flex items-center space-x-1"
                  title="Linked survey set"
                >
                  <LinkIcon className="w-3.5 h-3.5 mr-1" />
                  <span>+{assignment.linkedCount}</span>
                </span>
              )}
              {statusInfo.additionalInfo && (
                <span className="ml-2" title={statusInfo.additionalInfo.label}>
                  {statusInfo.additionalInfo.icon}
                </span>
              )}
              {assignment.status === 'completed' && !assignment.is_patient_initiated && (
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-500 ml-2 flex items-center"
                  title="Survey completed and locked"
                >
                  <Lock className="w-3 h-3 mr-1" />
                  Completed
                </span>
              )}
              {isDeactivated && (
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-500 ml-2 flex items-center"
                  title="This assessment can only be completed on-site"
                >
                  <Lock className="w-3 h-3 mr-1" />
                  On-site only
                </span>
              )}
              {isTimepointLocked && (
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-600 ml-2 flex items-center"
                  title={
                    assignment.timepoint_blocking_survey
                      ? `Complete "${assignment.timepoint_blocking_survey}" first`
                      : 'Complete the earlier assessment first'
                  }
                >
                  <Lock className="w-3 h-3 mr-1" />
                  Locked
                </span>
              )}
              {assignment.is_patient_initiated === true && (
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-600 ml-2"
                  title="Patient-prompted survey - can be taken multiple times"
                >
                  Anytime
                </span>
              )}
            </h3>
            {!hideStatusLabel && isDeactivated && (
              <p className="text-sm text-gray-500 mt-0.5">
                Available on-site only
              </p>
            )}
            {!hideStatusLabel && !isDeactivated && isTimepointLocked && (
              <p className="text-sm text-amber-600 mt-0.5">
                {assignment.timepoint_blocking_survey
                  ? `Complete "${assignment.timepoint_blocking_survey}" first`
                  : 'Complete the earlier assessment first'}
              </p>
            )}
            {!hideStatusLabel && !isDeactivated && !isTimepointLocked && (
              <p className="text-sm text-gray-500 flex items-center mt-0.5">
                <span className={`font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
                {assignment.linkedCount > 0 && (
                  <>
                    <span className="mx-2 text-gray-300">{'\u2022'}</span>
                    <span>{computeLinkedProgress(assignment)}</span>
                  </>
                )}
                {assignment.window_end_time && (
                  <>
                    <span className="mx-2 text-gray-300">{'\u2022'}</span>
                    <span>
                      Due{' '}
                      {new Date(new Date(assignment.window_end_time).getTime() - 1).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </>
                )}
              </p>
            )}
            {hideStatusLabel && (
              <p className="text-sm text-purple-500 mt-0.5 font-medium">
                Available whenever you need it
              </p>
            )}
          </div>
        </div>
        <ChevronRight
          className={`w-5 h-5 ml-4 flex-shrink-0 transform transition-all duration-300 ${
            isDisabled
              ? 'text-gray-300'
              : 'text-primary-600 group-hover:translate-x-1'
          }`}
        />
      </div>
    </motion.div>
  );
};
// Helpers
// FECOA-1513: a scheduled assessment whose site type is onsite must be shown
// on the web participant dashboard but cannot be started here (the participant
// has to be onsite). This is a web-client-only deactivation — the API still
// returns the assignment normally so mobile is unaffected. Only scheduled +
// onsite is gated; scheduled remote, daily (onsite/remote) and patient-prompted
// surveys are never deactivated.
const isScheduledOnsiteDeactivated = (assignment) => {
  if (!assignment) return false;
  const scheduleType = (assignment.schedule_type || '').toString().toLowerCase();
  const siteType = (assignment.site_type || '').toString().toLowerCase();
  const isOnsite = siteType === 'on-site' || siteType === 'onsite';
  return scheduleType === 'scheduled' && isOnsite;
};

const computeLinkedProgress = (assignment) => {
  let completedCount = 0;
  if (assignment.originalStatus === 'completed') completedCount += 1;
  if (assignment.linkedAssignments && assignment.linkedAssignments.length > 0) {
    completedCount += assignment.linkedAssignments.filter(
      (a) => a.originalStatus === 'completed'
    ).length;
  }
  return `${completedCount} of ${assignment.linkedCount + 1} complete`;
};

const getStatusInfo = (status, assignment) => {
  let additionalInfo = null;

  if (
    assignment &&
    assignment.linkedAssignments &&
    assignment.linkedAssignments.some((a) => a.status === 'completed') &&
    status === 'pending'
  ) {
    additionalInfo = {
      icon: <Clock className="w-4 h-4 text-blue-500 ml-1" />,
      label: 'Continuing',
    };
  }

  const info = {
    completed: {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      lightColor: 'text-emerald-500',
      label: 'Completed',
      progressColor: '#10b981',
    },
    pending: {
      icon: <AlertCircle className="w-5 h-5 text-amber-500" />,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      lightColor: 'text-amber-500',
      label: 'Pending',
      progressColor: '#f59e0b',
    },
  };

  const baseInfo = info[status] || {
    icon: null,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    lightColor: 'text-gray-500',
    label: status,
    progressColor: '#6b7280',
  };

  if (additionalInfo) {
    baseInfo.additionalInfo = additionalInfo;
  }

  return baseInfo;
};

// Anytime survey status — neutral purple icon, no Pending/Completed semantics
const getAnytimeStatusInfo = () => ({
  icon: <RefreshCw className="w-5 h-5 text-purple-500" />,
  color: 'text-purple-600',
  bgColor: 'bg-purple-50',
  lightColor: 'text-purple-500',
  label: 'Available',
  progressColor: '#9333ea',
});

// Empty State
const EmptyState = ({ message }) => (
  <div className="mt-3 bg-white border border-gray-200/80 rounded-xl shadow-sm">
    <div className="text-center py-16">
      <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <ClipboardList className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{message}</h3>
      <p className="text-gray-500">Check back later for new assignments</p>
    </div>
  </div>
);

// Main Component
const ParticipantDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [assignments, setAssignments] = useState([]);
  const [processedAssignments, setProcessedAssignments] = useState([]);
  const [completedToday, setCompletedToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [environment, setEnvironment] = useState(null);

  const bypassCache = true;

  // Normalize raw API data: shallow-copy each assignment and map open → pending.
  // This prevents mutation of the original API response objects.
  const normalizeAssignments = (data) =>
    (data || []).map((a) => ({
      ...a,
      status: a.status === 'open' ? 'pending' : a.status,
    }));

  useEffect(() => {
    fetchAssignments();
    fetchEnvironment();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    setError(null);
    try {
      // FECOA-1226: fetch assignments and today's completions together so the
      // Completed count reflects patient-prompted completions, whose windows
      // get reset to OPEN server-side after each pass.
      const [data, completions] = await Promise.all([
        surveyService.getMyAssignments(bypassCache),
        surveyService.getMyCompletedToday(),
      ]);
      const normalized = normalizeAssignments(data);
      setAssignments(normalized);
      setProcessedAssignments(processLinkedSurveys(normalized));
      setCompletedToday(completions || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to load your surveys. Please try again later.');
      console.error('Error fetching assignments:', err);
      setLoading(false);
    }
  };

  const fetchEnvironment = async () => {
    try {
      const versionInfo = await versionService.getVersionInfo();
      setEnvironment(versionInfo?.environment);
    } catch (err) {
      console.error('Error fetching environment:', err);
    }
  };

  const applyFakeDate = async (bypassCache, fake_date) => {
    try {
      const [data, completions] = await Promise.all([
        surveyService.getMyAssignments(bypassCache, fake_date),
        surveyService.getMyCompletedToday(fake_date),
      ]);
      const normalized = normalizeAssignments(data);
      setAssignments(normalized);
      setProcessedAssignments(processLinkedSurveys(normalized));
      setCompletedToday(completions || []);
    } catch (err) {
      setError('Failed to load your surveys. Please try again later.');
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  // Expects already-normalized data (open → pending done before calling).
  const processLinkedSurveys = (assignmentList) => {
    if (!assignmentList || assignmentList.length === 0) {
      return [];
    }

    const linkedSurveys = assignmentList.filter((a) => a.survey_is_linked === true);
    const standaloneSurveys = assignmentList.filter((a) => a.survey_is_linked !== true);

    if (linkedSurveys.length === 0) {
      return assignmentList;
    }

    const linkedBySurvey = new Map(
      linkedSurveys.map((a) => [String(a.survey), a])
    );

    const resolveChainHeadSurveyId = (assignment) => {
      let current = assignment;
      const visited = new Set();
      while (current.is_linked_survey != null && !visited.has(String(current.survey))) {
        visited.add(String(current.survey));
        const parent = linkedBySurvey.get(String(current.is_linked_survey));
        if (!parent) break;
        current = parent;
      }
      return String(current.survey);
    };

    const linkedSurveySets = {};
    linkedSurveys.forEach((assignment) => {
      const headId = resolveChainHeadSurveyId(assignment);
      if (!linkedSurveySets[headId]) {
        linkedSurveySets[headId] = [];
      }
      linkedSurveySets[headId].push(assignment);
    });

    const processedLinkedSurveys = [];

    Object.values(linkedSurveySets).forEach((surveySet) => {
      if (surveySet.length === 0) return;

      surveySet.sort((a, b) => {
        const rankA = typeof a.survey_rank === 'number' ? a.survey_rank : 0;
        const rankB = typeof b.survey_rank === 'number' ? b.survey_rank : 0;
        return rankA - rankB;
      });

      const surveysWithOriginalStatus = surveySet.map((survey) => ({
        ...survey,
        originalStatus: survey.status,
      }));

      const primaryAssignment = { ...surveysWithOriginalStatus[0] };

      primaryAssignment.linkedCount = surveySet.length - 1;
      primaryAssignment.linkedSurveyIds = surveySet.slice(1).map((a) => a.survey);
      primaryAssignment.linkedAssignments = surveysWithOriginalStatus.slice(1);

      const allCompleted = surveySet.every((a) => a.status === 'completed');

      if (allCompleted) {
        primaryAssignment.status = 'completed';
      } else {
        primaryAssignment.status = 'pending';
        const nextToComplete = surveySet.find((a) => a.status !== 'completed');
        if (nextToComplete) {
          primaryAssignment.nextToCompleteId = nextToComplete.id;
        } else {
          primaryAssignment.nextToCompleteId = surveySet[0].id;
        }
      }

      processedLinkedSurveys.push(primaryAssignment);
    });

    return [...processedLinkedSurveys, ...standaloneSurveys];
  };

  const handleSurveyClick = async (assignmentId) => {
    try {
      const assignment = processedAssignments.find((a) => a.id === assignmentId);

      if (!assignment) {
        console.error('Assignment not found:', assignmentId);
        return;
      }

      // FECOA-1513: scheduled onsite assessments are deactivated on the web
      // dashboard — block navigation even if a click slips through.
      if (isScheduledOnsiteDeactivated(assignment)) {
        console.log('Scheduled onsite assessment is deactivated on web, preventing navigation');
        return;
      }

      // FECOA-1333: a survey locked by strict within-timepoint ordering cannot
      // be started until the earlier assessment in its timepoint is completed.
      if (assignment.timepoint_locked) {
        console.log('Survey locked by timepoint ordering, preventing navigation');
        return;
      }

      if (assignment.status === 'completed' && !assignment.is_patient_initiated) {
        console.log('Survey already completed, preventing navigation');
        return;
      }

      if (assignment.is_patient_initiated) {
        console.log('Patient-prompted survey - allowing navigation for re-take');
      }

      if (assignment.nextToCompleteId) {
        console.log('Navigating to next survey in linked set:', assignment.nextToCompleteId);

        const nextAssignment = assignments.find((a) => a.id === assignment.nextToCompleteId);
        if (nextAssignment?.status === 'pending') {
          await surveyService.updateAssignmentStatus(assignment.nextToCompleteId, 'open');
        }

        navigate(`/survey-taking/${assignment.nextToCompleteId}`);
        return;
      }

      if (assignment?.status === 'pending') {
        await surveyService.updateAssignmentStatus(assignmentId, 'open');
      }

      navigate(`/survey-taking/${assignmentId}`);
    } catch (err) {
      console.error('Error updating assignment status:', err);
      navigate(`/survey-taking/${assignmentId}`);
    }
  };

  // ── Derived Data ──────────────────────────────────────────────────────
  // Split processed assignments into scheduled vs patient-prompted.
  //
  // Backend orders by Schedule Builder rank (FECOA-1153). Re-apply that sort
  // here because `processLinkedSurveys` rebuilds the list and can perturb order.
  const { scheduledSurveys, patientPromptedSurveys } = useMemo(() => {
    const compareAssignments = (a, b) => {
      const rankA = typeof a.survey_rank === 'number' ? a.survey_rank : 0;
      const rankB = typeof b.survey_rank === 'number' ? b.survey_rank : 0;
      if (rankA !== rankB) return rankA - rankB;

      const startA = a?.window_start_time ? new Date(a.window_start_time).getTime() : Number.POSITIVE_INFINITY;
      const startB = b?.window_start_time ? new Date(b.window_start_time).getTime() : Number.POSITIVE_INFINITY;
      if (startA !== startB) return startA - startB;

      const nameA = (a?.survey_name || '').toString();
      const nameB = (b?.survey_name || '').toString();
      return nameA.localeCompare(nameB);
    };

    const scheduled = processedAssignments
      .filter((a) => a.is_patient_initiated !== true)
      .slice()
      .sort(compareAssignments);
    const prompted = processedAssignments
      .filter((a) => a.is_patient_initiated === true)
      .slice()
      .sort(compareAssignments);
    return { scheduledSurveys: scheduled, patientPromptedSurveys: prompted };
  }, [processedAssignments]);

  // Shape today's CompletedSurvey records into SurveyCard-friendly objects so
  // the "Today's Completed" filter can render every completion (including
  // repeated patient-prompted takes) the same way pending cards are rendered.
  // Force is_patient_initiated=false so SurveyCard treats the row as locked
  // (it's a historical completion record, not a take-again entry point).
  const completedTodayCards = useMemo(
    () =>
      (completedToday || []).map((c) => ({
        id: c.id,
        survey: c.survey,
        survey_name: c.survey_name,
        status: 'completed',
        is_patient_initiated: false,
        schedule_type: c.schedule_type,
        window_end_time: c.completed_at,
        linkedCount: 0,
      })),
    [completedToday]
  );

  // "Today's Pending" comes from the live assignments feed; "Today's Completed"
  // comes from CompletedSurvey because patient-prompted windows are reset to
  // OPEN after each completion and no longer carry a completed status.
  const filteredScheduled = useMemo(() => {
    if (selectedTimeframe === 'completed') return completedTodayCards;
    if (selectedTimeframe === 'pending') {
      return scheduledSurveys.filter((a) => a.status === 'pending');
    }
    return scheduledSurveys;
  }, [scheduledSurveys, completedTodayCards, selectedTimeframe]);

  // ── Stat cards ────────────────────────────────────────────────────────
  // "Today's Surveys" = total count of ALL assignments (scheduled + anytime)
  // "Completed"       = count of CompletedSurvey rows recorded today, which
  //                      is the only source that captures repeated
  //                      patient-prompted completions (FECOA-1226).
  // "Pending"         = only SCHEDULED surveys that are pending (anytime
  //                      have no meaningful pending state).
  const stats = useMemo(() => {
    const scheduledOnly = assignments.filter((a) => a.is_patient_initiated !== true);

    return [
      {
        icon: <BookOpen className="w-5 h-5" />,
        label: "Today's Surveys",
        value: assignments.length,
        accent: 'primary',
      },
      {
        icon: <CheckCircle2 className="w-5 h-5" />,
        label: 'Completed',
        value: completedToday.length,
        accent: 'emerald',
      },
      {
        icon: <AlertCircle className="w-5 h-5" />,
        label: 'Pending',
        value: scheduledOnly.filter((a) => a.status === 'pending').length,
        accent: 'amber',
      },
    ];
  }, [assignments, completedToday]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <p className="text-lg text-gray-800 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 px-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-medium"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="space-y-8">
          {(user?.is_superuser || (user?.role === 'Program Admin' && (environment === 'development' || environment === 'uat'))) && (
            <DateMocker onApplyFakeDate={applyFakeDate} bypassCache={bypassCache} />
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {stats.map((stat, i) => (
              <StatCard key={i} {...stat} />
            ))}
          </div>

          {/* Scheduled Surveys */}
          <section>
            <SectionHeader
              title="Your Scheduled Surveys"
              subtitle="Track and complete your assigned surveys"
            >
              <div className="flex flex-wrap gap-2">
                {['all', 'pending', 'completed'].map((timeframe) => (
                  <button
                    key={timeframe}
                    onClick={() => setSelectedTimeframe(timeframe)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedTimeframe === timeframe
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {timeframe === 'all'
                      ? "Today's Surveys"
                      : `Today's ${timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}`}
                  </button>
                ))}
              </div>
            </SectionHeader>

            {filteredScheduled.length === 0 ? (
              <EmptyState message="No surveys match this filter" />
            ) : (
              <div className="mt-3 space-y-3">
                {filteredScheduled.map((assignment) => {
                  const statusInfo = getStatusInfo(assignment.status, assignment);
                  return (
                    <SurveyCard
                      key={assignment.id}
                      assignment={assignment}
                      statusInfo={statusInfo}
                      isDeactivated={isScheduledOnsiteDeactivated(assignment)}
                      onClick={() => handleSurveyClick(assignment.id)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {/* Patient-Prompted Surveys — dedicated "Available Anytime" section.
              Only visible when no specific filter is active (i.e. showing "all").
              When user clicks "Today's Pending" or "Today's Completed", this
              section hides so anytime surveys never leak into filtered results. */}
          {patientPromptedSurveys.length > 0 && selectedTimeframe === 'all' && (
            <section>
              <SectionHeader
                title="Available Anytime"
                subtitle="These surveys are available whenever you need them"
              />
              <div className="mt-3 space-y-3">
                {patientPromptedSurveys.map((assignment) => {
                  const statusInfo = getAnytimeStatusInfo();
                  return (
                    <SurveyCard
                      key={assignment.id}
                      assignment={assignment}
                      statusInfo={statusInfo}
                      hideStatusLabel
                      onClick={() => handleSurveyClick(assignment.id)}
                    />
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
  );
};

export default ParticipantDashboard;